import { createAction } from '@reduxjs/toolkit';
import { Languages, TranslatableField } from 'common/types';
import {
	TranslationGroup,
	TranslationsState,
	SaveTranslationPayload,
} from 'reducers/TranslationsReducer';
import { createAppThunk } from 'services/utils/redux';
import { ReduxState } from 'services/types';
import { api } from 'common/frontend/api';
import * as TranslationsSelectors from 'selectors/TranslationsSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import produce from 'immer';
import { isEqual } from 'lodash';
import { isCustomShopPaymentMethodObject } from 'common/modules/payments/paymentMethods';

export const setFieldValue = createAction<{
	group: TranslationGroup;
	docId: string;
	fieldId: string;
	value: string;
	language: Languages;
}>('Translations/SET_FIELD_VALUE');

export const commitFieldValue = createAction<{ fieldId: string }>(
	'Translations/COMMIT_FIELD_VALUE',
);

export const setLanguage = createAction<Languages>('Translations/SET_LANGUAGE');
export const setDefaultLanguage = createAction<Languages>('Translations/SET_DEFAULT_LANGUAGE');
export const setTranslations = createAction<TranslationsState['translations']>(
	'Translations/SET_TRANSLATIONS',
);

export const saveTranslation = createAppThunk<void, SaveTranslationPayload, { state: ReduxState }>(
	'Translations/SAVE_TRANSLATION',
	async (args, thunkAPI) => {
		try {
			await saveFieldValue(args.field, thunkAPI.getState());
			thunkAPI.fulfillWithValue(args);
		} catch (err) {
			thunkAPI.rejectWithValue(args);
		}
	},
);

export const retryAllFailedSaves = createAppThunk(
	'Translations/RETRY_FAILED_SAVES',
	(args, thunkAPI) => {
		const state = thunkAPI.getState();
		const errors = TranslationsSelectors.errorFields(state);

		return Promise.all(errors.map((field) => thunkAPI.dispatch(saveTranslation(field))));
	},
);

const saveFieldValue = async (field: TranslatableField, state: ReduxState) => {
	const fieldValue = field.value;
	if (!fieldValue?.def) return;

	switch (field.opts.type) {
		case 'productName': {
			const { productId } = field.opts.args;
			return api().products.doc(productId).update({
				name: fieldValue,
			});
		}
		case 'productDescription': {
			const { productId } = field.opts.args;
			return api().products.doc(productId).update({
				description: fieldValue,
			});
		}
		case 'productVariantProperty': {
			const { productId, propertyId } = field.opts.args;
			const product = StockSelectors.stockProductsById(state)[productId];
			if (!product?.variants.properties) return;

			const updatedVariantProperties = product.variants.properties.map((property) =>
				property.id === propertyId ? { ...property, name: fieldValue } : property,
			);

			const hasChanged = !isEqual(updatedVariantProperties, product.variants.properties);

			return (
				hasChanged &&
				api().products.doc(productId).update({
					'variants.properties': updatedVariantProperties,
				})
			);
		}
		case 'productVariantValue': {
			const { productId, variantId, propertyId } = field.opts.args;
			const product = StockSelectors.stockProductsById(state)[productId];
			if (!product) return;

			const updatedProductVariants = product.variants.options.map((variant) => {
				if (variant.id !== variantId) return variant;
				return {
					...variant,
					values: {
						...variant.values,
						[propertyId]: fieldValue,
					},
				};
			});

			const hasChanged = !isEqual(updatedProductVariants, product.variants.options);

			return (
				hasChanged &&
				api().products.doc(productId).update({
					'variants.options': updatedProductVariants,
				})
			);
		}
		case 'productPricingLabel': {
			const { productId, index } = field.opts.args;
			const product = StockSelectors.stockProductsById(state)[productId];
			if (!product?.pricing) return;

			const updatedProductPricing = produce(product.pricing, (draft) => {
				draft[index].label = fieldValue;
			});

			const hasChanged = !isEqual(updatedProductPricing, product.pricing);

			return (
				hasChanged &&
				api().products.doc(productId).update({
					pricing: updatedProductPricing,
				})
			);
		}
		case 'productSegmentPricingLabel': {
			const { productId, segmentPricingIndex, index } = field.opts.args;
			const product = StockSelectors.stockProductsById(state)[productId];

			const updatedProductSegmentPricings = produce(product.segmentPricings, (draft) => {
				const row = draft?.[segmentPricingIndex]?.pricingTable?.[index];
				if (!!row?.label?.def) row.label = fieldValue;
			});

			const hasChanged = !isEqual(updatedProductSegmentPricings, product.segmentPricings);

			return (
				hasChanged &&
				api().products.doc(productId).update({
					segmentPricings: updatedProductSegmentPricings,
				})
			);
		}
		case 'categoryName': {
			const { categoryId } = field.opts.args;
			return api().productCategories.doc(categoryId).update({
				name: fieldValue,
			});
		}
		case 'categoryDescription': {
			const { categoryId } = field.opts.args;
			return api().productCategories.doc(categoryId).update({
				description: fieldValue,
			});
		}
		case 'storeDescription': {
			const { shopId, storeId } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			if (storeId === shopPublicInfo.visitingAddress?.id) {
				return api().shops.doc(shopId).public.update({
					'visitingAddress.description': fieldValue,
				});
			} else if (!!shopPublicInfo.shopLocations) {
				const updatedShopLocations = produce(shopPublicInfo.shopLocations, (draft) => {
					const storeToUpdate = draft.find((s) => s.id === storeId);
					if (!storeToUpdate) return;
					storeToUpdate.description = fieldValue;
				});

				const hasChanged = !isEqual(updatedShopLocations, shopPublicInfo.shopLocations);
				return (
					hasChanged &&
					api().shops.doc(shopId).public.update({
						shopLocations: updatedShopLocations,
					})
				);
			}
			break;
		}
		case 'storeAdditionalInformation': {
			const { shopId, storeId } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			if (storeId === shopPublicInfo.visitingAddress?.id) {
				return api().shops.doc(shopId).public.update({
					'visitingAddress.additionalInformation': fieldValue,
				});
			} else if (!!shopPublicInfo.shopLocations) {
				const updatedAdditionalInformation = produce(shopPublicInfo.shopLocations, (draft) => {
					const storeToUpdate = draft.find((s) => s.id === storeId);
					if (!storeToUpdate) return;
					storeToUpdate.additionalInformation = fieldValue;
				});

				const hasChanged = !isEqual(updatedAdditionalInformation, shopPublicInfo.shopLocations);
				return (
					hasChanged &&
					api().shops.doc(shopId).public.update({
						shopLocations: updatedAdditionalInformation,
					})
				);
			}
			return;
		}
		case 'storeCheckoutCommentField': {
			const { shopId, storeId } = field.opts.args;
			return api()
				.shops.doc(shopId)
				.public.update({
					[`checkoutCommentField.${storeId}.label` as `checkoutCommentField.${123}.label`]: fieldValue,
				});
		}
		case 'storeInStoreEndText': {
			const { shopId, storeId, index } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			if (storeId === shopPublicInfo.visitingAddress?.id) {
				const inStoreEndText = produce(shopPublicInfo.inStoreEndText?.defaultValue, (draft) => {
					if (!!draft?.[index]?.text?.def) draft[index].text = fieldValue;
				});

				const hasChanged = !isEqual(inStoreEndText, shopPublicInfo.inStoreEndText?.defaultValue);
				if (!inStoreEndText) return;
				return (
					hasChanged &&
					api().shops.doc(shopId).public.update({
						'inStoreEndText.defaultValue': inStoreEndText,
					})
				);
			} else {
				const inStoreEndText = produce(
					shopPublicInfo.inStoreEndText?.location?.[storeId],
					(draft) => {
						if (!!draft?.[index]?.text?.def) draft[index].text = fieldValue;
					},
				);

				const hasChanged = !isEqual(inStoreEndText, shopPublicInfo.inStoreEndText?.location);
				if (!inStoreEndText) return;
				return (
					hasChanged &&
					api()
						.shops.doc(shopId)
						.public.update({
							[`inStoreEndText.location.${storeId}` as `inStoreEndText.location.${123}`]: inStoreEndText,
						})
				);
			}
		}
		case 'pricingTableLabel': {
			const { shopId, index, pricingTableName } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			const pricingTables = produce(shopPublicInfo.pricingTables, (draft) => {
				const row = draft?.[pricingTableName]?.[index];
				if (!!row?.label?.def) row.label = fieldValue;
			});

			const hasChanged = !isEqual(pricingTables, shopPublicInfo.pricingTables);

			if (!pricingTables) return;

			return (
				hasChanged &&
				api().shops.doc(shopId).public.update({
					pricingTables,
				})
			);
		}
		case 'marketingConsentText': {
			const { shopId } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo?.marketingConsentText) return;

			return api()
				.shops.doc(shopId)
				.public.update({
					marketingConsentText: {
						...shopPublicInfo.marketingConsentText,
						text: fieldValue,
					},
				});
		}
		case 'deliveryOptionName': {
			const { deliveryOptionId } = field.opts.args;

			return api().deliveryOptions.doc(deliveryOptionId).update({
				name: fieldValue,
			});
		}
		case 'deliveryOptionDescription': {
			const { deliveryOptionId } = field.opts.args;

			return api().deliveryOptions.doc(deliveryOptionId).update({
				description: fieldValue,
			});
		}
		case 'deliveryOptionReturnInstructions': {
			const { deliveryOptionId } = field.opts.args;

			return api().deliveryOptions.doc(deliveryOptionId).update({
				returnInstructions: fieldValue,
			});
		}
		case 'deliveryCarrierName': {
			const { carrierId } = field.opts.args;

			return api().carriers.doc(carrierId).update({
				name: fieldValue,
			});
		}

		case 'skidataDiscountGroup': {
			const { segmentId, categoryId } = field.opts.args;
			const liftTicketCategory = StockSelectors.liftTicketCategory(state);

			if (!liftTicketCategory) return;

			const updatedSegmentMapping = produce(liftTicketCategory.segmentMapping, (draft) => {
				const segment = draft?.find((d) => d?.externalSegmentId === segmentId);
				if (!!segment) segment.name = fieldValue;
			});

			const hasChanged = !isEqual(updatedSegmentMapping, liftTicketCategory?.segmentMapping);

			return (
				hasChanged &&
				api().productCategories.doc(categoryId).update({
					segmentMapping: updatedSegmentMapping,
				})
			);
		}

		case 'manualPaymentMethodLabel': {
			const { shopId, methodId } = field.opts.args;
			const manualPaymentMethods = ShopSelectors.getManualPaymentMethods(state);
			const activePaymentMethods = ShopSelectors.getActivePaymentMethods(state);

			const updatedManualPaymentMethods = produce(manualPaymentMethods, (draft) => {
				const method = draft.find((method) => method.id === methodId);
				if (!!method) method.label = fieldValue;
			});

			const updatedActivePaymentMethods = produce(activePaymentMethods, (draft) => {
				const method = draft.find((m) => {
					return isCustomShopPaymentMethodObject(m) && m.details.id === methodId;
				});

				if (!!method && isCustomShopPaymentMethodObject(method)) method.details.label = fieldValue;
			});

			return Promise.all([
				api().shops.doc(shopId).update({
					manualPaymentMethods: updatedManualPaymentMethods,
				}),
				api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				}),
			]);
		}

		case 'manualPaymentMethodDescription': {
			const { shopId, methodId } = field.opts.args;
			const manualPaymentMethods = ShopSelectors.getManualPaymentMethods(state);
			const activePaymentMethods = ShopSelectors.getActivePaymentMethods(state);

			const updatedManualPaymentMethods = produce(manualPaymentMethods, (draft) => {
				const method = draft.find((method) => method.id === methodId);
				if (!!method) method.description = fieldValue;
			});

			const updatedActivePaymentMethods = produce(activePaymentMethods, (draft) => {
				const method = draft.find((m) => {
					return isCustomShopPaymentMethodObject(m) && m.details.id === methodId;
				});

				if (!!method && isCustomShopPaymentMethodObject(method))
					method.details.description = fieldValue;
			});

			return Promise.all([
				api().shops.doc(shopId).update({
					manualPaymentMethods: updatedManualPaymentMethods,
				}),
				api().shops.doc(shopId).public.update({
					activePaymentMethods: updatedActivePaymentMethods,
				}),
			]);
		}

		case 'footerAboutHeading': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.footer.about.heading` as `layouts.ONLINE.locations.${123}.footer.about.heading`]: fieldValue,
				});
		}

		case 'footerAboutDescription': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.footer.about.description` as `layouts.ONLINE.locations.${123}.footer.about.description`]: fieldValue,
				});
		}

		case 'footerMenuHeading': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.footer.menu.heading` as `layouts.ONLINE.locations.${123}.footer.menu.heading`]: fieldValue,
				});
		}

		case 'footerLinksHeading': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.footer.links.heading` as `layouts.ONLINE.locations.${123}.footer.links.heading`]: fieldValue,
				});
		}

		case 'footerLinkLabel': {
			const { shopId, storeId, index } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.footer.links.linkObjects.${index}.label` as `layouts.ONLINE.locations.${123}.footer.links.linkObjects.${456}.label`]: fieldValue,
				});
		}

		case 'featuredImageHeading': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.featuredImage.heading` as `layouts.ONLINE.locations.${123}.featuredImage.heading`]: fieldValue,
				});
		}

		case 'featuredImageDescription': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.featuredImage.description` as `layouts.ONLINE.locations.${123}.featuredImage.description`]: fieldValue,
				});
		}

		case 'featuredImageButtonLabel': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.featuredImage.button.label` as `layouts.ONLINE.locations.${123}.featuredImage.button.label`]: fieldValue,
				});
		}

		case 'aboutHeading': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.about.heading` as `layouts.ONLINE.locations.${123}.about.heading`]: fieldValue,
				});
		}

		case 'aboutTextContent': {
			const { shopId, storeId } = field.opts.args;

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}.about.textContent` as `layouts.ONLINE.locations.${123}.about.textContent`]: fieldValue,
				});
		}

		case 'aboutCustomImageHeading': {
			const { shopId, storeId, sectionIndex, contentIndex } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			const updatedLayoutSettings = produce(
				shopPublicInfo.layouts?.ONLINE?.locations?.[storeId],
				(draft) => {
					const customImage =
						draft?.about?.customContent?.sections?.[sectionIndex]?.content?.[contentIndex];
					if (customImage?.type === 'image' && !!customImage?.heading?.def) {
						customImage.heading = fieldValue;
					}
				},
			);

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}` as `layouts.ONLINE.locations.${123}`]: updatedLayoutSettings,
				});
		}

		case 'aboutCustomImageDescription': {
			const { shopId, storeId, sectionIndex, contentIndex } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			const updatedLayoutSettings = produce(
				shopPublicInfo.layouts?.ONLINE?.locations?.[storeId],
				(draft) => {
					const customImage =
						draft?.about?.customContent?.sections?.[sectionIndex]?.content?.[contentIndex];
					if (customImage?.type === 'image' && !!customImage?.description?.def) {
						customImage.description = fieldValue;
					}
				},
			);

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}` as `layouts.ONLINE.locations.${123}`]: updatedLayoutSettings,
				});
		}

		case 'aboutCustomTextHeading': {
			const { shopId, storeId, sectionIndex, contentIndex } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			const updatedLayoutSettings = produce(
				shopPublicInfo.layouts?.ONLINE?.locations?.[storeId],
				(draft) => {
					const customText =
						draft?.about?.customContent?.sections?.[sectionIndex]?.content?.[contentIndex];

					if (customText?.type === 'text' && !!customText?.heading?.def) {
						customText.heading = fieldValue;
					}
				},
			);

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}` as `layouts.ONLINE.locations.${123}`]: updatedLayoutSettings,
				});
		}

		case 'aboutCustomTextBody': {
			const { shopId, storeId, sectionIndex, contentIndex } = field.opts.args;
			const shopPublicInfo = ShopSelectors.activeShopPublicInfo(state);
			if (!shopPublicInfo) return;

			const updatedLayoutSettings = produce(
				shopPublicInfo.layouts?.ONLINE?.locations?.[storeId],
				(draft) => {
					const customText =
						draft?.about?.customContent?.sections?.[sectionIndex]?.content?.[contentIndex];

					if (customText?.type === 'text' && !!customText?.body?.def) {
						customText.body = fieldValue;
					}
				},
			);

			return api()
				.shops.doc(shopId)
				.public.update({
					[`layouts.ONLINE.locations.${storeId}` as `layouts.ONLINE.locations.${123}`]: updatedLayoutSettings,
				});
		}

		default:
			return;
	}
};
